// src/lib/caution-amount.js — LOT 2 (caution par plateforme) — fondation.
//
// ⚠️ SOURCE UNIQUE pour le serveur ET l'écran (F10 phase 2, 03/10). Écrit en
// CommonJS pur, zéro dépendance ; le serveur le lit par le relais
// `lib/caution-amount.js`. Ne rien ajouter qui ne soit chargeable des deux
// côtés (garde : src/lib/doublons-lib-conformite.test.js, « sources uniques »).
//
// Résolution PURE du MONTANT de caution d'un logement résolu depuis le cfg
// (self d'abord, parentLog pour les chambres sous-loc lc1/lc2/lc3).
// Renvoie un Number fini > 0, ou null s'il n'y a pas de caution.
//
// ⚖ RÈGLE UNIQUE — décisions figées de Dimitri (07/10) :
//   - champ VIDE (absent, null, chaîne vide ou blancs) → on prend la caution
//     du logement parent ;
//   - caution réglée à 0 (0, "0") → « pas de caution » : null, et on NE
//     remonte PAS au parent ;
//   - montant NÉGATIF (nombre ou texte) → c'est une ERREUR de saisie, pas
//     « pas de caution » : on le traite comme un champ vide (parent) ET on le
//     SIGNALE (diagnostiquerCaution → anomalies ; côté serveur, alerte
//     journalisée par lib/caution-anomalie.js::signalerAnomaliesCaution, appelée
//     par getCautionAmount et les deux portes du site direct ; l'écriture
//     d'un négatif est refusée par POST /api/cfg) ;
//   - valeur > 0 → cette valeur.
//   Une valeur illisible (« abc », NaN, Infinity, autre type) compte comme
//   vide (règle historique : on remonte au parent).
//
// TOUS les chemins passent par ici (07/10, lot argent 3d) : api/sites.js
// (devis, demande, E1/E2/E3, montant affiché), lib/cfg-reader-server.js
// ::getCautionAmount (montant de la préautorisation Stripe quand la résa n'a
// pas encore de montant verrouillé), le contrat (lib/ et src/lib/
// contrat-html.js), le lien de caution automatique (lib/messages-engine.js,
// lib/livret-sections-handlers.js), la fiche opérateur, l'état de FBZ et le
// badge admin (src/lib/caution-helpers.js::hasCautionAmount). Avant, deux
// règles cohabitaient (0 accepté côté Stripe, 0 → parent côté site).
//
// Fonction PURE, sans I/O, testable sans env.

// Lit UN champ caution : { etat: "vide" | "zero" | "negatif" | "montant", n? }.
// Argent 1f (relecture) : un texte ne vaut « 0 voulu » que s'il s'écrit
// exactement 0, 0.00 ou 0,00 (espaces autour tolérés) — jamais « 0x0 »,
// « 0e5 », « 0b0 », « -0 » ni « \t0\n », qui comptent comme illisibles (vide).
const _ZERO_TEXTE = /^ *(?:0|0\.00|0,00) *$/;

function _lireChamp(v) {
  if (typeof v === "string") {
    if (v.trim() === "") return { etat: "vide" };
    if (_ZERO_TEXTE.test(v)) return { etat: "zero", n: 0 };
  } else if (typeof v !== "number") {
    return { etat: "vide" }; // absent, null, autre type
  }
  const n = Number(v);
  if (!Number.isFinite(n)) return { etat: "vide" };
  if (n === 0) {
    // Nombre 0 → voulu ; texte valant 0 hors des trois formes → illisible.
    return typeof v === "number" ? { etat: "zero", n } : { etat: "vide" };
  }
  if (n < 0) return { etat: "negatif", n };
  return { etat: "montant", n };
}

// ── CAUTION PAR PALIERS (décisions de Dimitri du 30/10, n° 16) ─────────────
//
// Réglage PAR LOGEMENT, à la même place que `caution_amount` (cfg du
// logement, chambre puis parent) :
//   caution_paliers: [
//     { nuits_max: 4,    montant: 50 },   // séjours de 1 à 4 nuits
//     { nuits_max: null, montant: 80 },   // au-delà (le dernier, toujours)
//   ]
// Un seul palier { nuits_max: null, montant } = « tous les séjours ». Montants
// en EUROS ENTIERS ≥ 0 (0 = pas de caution pour ces durées) ; durées
// entières ≥ 1, strictement croissantes ; le dernier palier n'a pas de durée.
// La saisie se valide par src/lib/caution-reglage.js (même source).
//
// RÉTROCOMPATIBLE, à l'octet près : sans durée demandée (`{ nuits }` absent),
// ou sans paliers à un niveau, la résolution est EXACTEMENT celle d'avant
// (champ `caution_amount`, vide → parent, 0 → pas de caution). Les paliers ne
// sont lus que si l'appelant donne la durée du séjour.
//
// Paliers illisibles (forme cassée, saisie à la main) → ignorés à ce niveau,
// comme une valeur illisible l'est déjà (on lit `caution_amount`, puis le
// parent) ET signalés (anomalie `paliers_illisibles`).
// Durée donnée mais illisible (0, NaN, 2,5 nuits) avec des paliers → AUCUN
// montant (null, et pas « 0 voulu ») : on ne devine jamais une caution ;
// anomalie `duree_illisible`.

// Plafond d'un palier (relecture R4 de #1738), écrit UNE fois : une caution
// au-delà est une faute de frappe (un zéro de trop), pas un réglage. Les
// cautions réelles valent 50 € ; l'écran d'aujourd'hui conseille 50 à 2 000 €.
// Rangé au-delà → paliers illisibles (ignorés et signalés) ; saisi au-delà →
// refusé avec sa phrase (src/lib/caution-reglage.js).
const CAUTION_MONTANT_MAX_EUR = 5000;

function _estEntierPositif(v) {
  return typeof v === "number" && Number.isInteger(v) && v >= 1;
}

/**
 * Lit un champ `caution_paliers` tel qu'il est rangé.
 * @returns {{ etat: "absent" } | { etat: "illisible" } |
 *           { etat: "paliers", paliers: Array<{nuits_max: number|null, montant: number}> }}
 */
function lirePaliers(brut) {
  if (brut == null) return { etat: "absent" };
  if (!Array.isArray(brut)) return { etat: "illisible" };
  if (brut.length === 0) return { etat: "absent" };
  let precedent = 0;
  const paliers = [];
  for (let i = 0; i < brut.length; i++) {
    const p = brut[i];
    const dernier = i === brut.length - 1;
    if (!p || typeof p !== "object") return { etat: "illisible" };
    const m = p.montant;
    if (
      typeof m !== "number" ||
      !Number.isInteger(m) ||
      m < 0 ||
      m > CAUTION_MONTANT_MAX_EUR
    ) {
      return { etat: "illisible" };
    }
    if (dernier) {
      if (p.nuits_max != null) return { etat: "illisible" };
      paliers.push({ nuits_max: null, montant: m });
    } else {
      if (!_estEntierPositif(p.nuits_max) || p.nuits_max <= precedent) {
        return { etat: "illisible" };
      }
      precedent = p.nuits_max;
      paliers.push({ nuits_max: p.nuits_max, montant: m });
    }
  }
  return { etat: "paliers", paliers };
}

/**
 * Le palier qui s'applique à un séjour de `nuits` nuits : le premier dont la
 * durée maximale couvre le séjour, sinon le dernier (« au-delà »).
 * @returns {{nuits_max: number|null, montant: number}|null} null si `nuits`
 *   n'est pas un nombre entier de nuits ≥ 1.
 */
function palierPourDuree(paliers, nuits) {
  if (!_estEntierPositif(nuits) || !Array.isArray(paliers) || !paliers.length) {
    return null;
  }
  for (const p of paliers) {
    if (p.nuits_max != null && nuits <= p.nuits_max) return p;
  }
  return paliers[paliers.length - 1];
}

// Résolution UNIQUE (montant, 0 voulu, anomalies) : les trois fonctions
// publiques ci-dessous en sont des vues.
function _resoudre(logFromCfg, opts) {
  const anomalies = [];
  const avecDuree = !!(opts && typeof opts === "object" && "nuits" in opts);
  const nuits = avecDuree ? opts.nuits : undefined;
  const niveaux = [
    ["logement", logFromCfg],
    ["parent", logFromCfg?.parentLog],
  ];
  for (const [niveau, noeud] of niveaux) {
    if (avecDuree) {
      const lu = lirePaliers(noeud?.caution_paliers);
      if (lu.etat === "illisible") {
        anomalies.push({ niveau, motif: "paliers_illisibles" });
      } else if (lu.etat === "paliers") {
        const p = palierPourDuree(lu.paliers, nuits);
        if (!p) {
          anomalies.push({ niveau, motif: "duree_illisible", valeur: nuits });
          return { montant: null, zero: false, anomalies };
        }
        return {
          montant: p.montant > 0 ? p.montant : null,
          zero: p.montant === 0,
          anomalies,
        };
      }
    }
    const c = _lireChamp(noeud?.caution_amount);
    if (c.etat === "negatif") {
      // Erreur de saisie : signalée, puis traitée comme un champ vide.
      anomalies.push({ niveau, motif: "montant_negatif", valeur: c.n });
      continue;
    }
    if (c.etat === "vide") continue;
    return {
      montant: c.etat === "montant" ? c.n : null,
      zero: c.etat === "zero",
      anomalies,
    };
  }
  return { montant: null, zero: false, anomalies };
}

/**
 * Montant de caution ET anomalies de saisie rencontrées en le résolvant.
 * Fonction compagne de resolveCautionFromLog (même règle, même résultat).
 *
 * @param {object|null} logFromCfg — log résolu via flattenLogs (peut porter parentLog)
 * @param {{ nuits: number }} [opts] — durée du séjour : active les paliers
 * @returns {{ montant: number|null,
 *             anomalies: Array<{ niveau: "logement"|"parent",
 *                                motif: "montant_negatif"|"paliers_illisibles"|"duree_illisible",
 *                                valeur?: number }> }}
 */
function diagnostiquerCaution(logFromCfg, opts) {
  const { montant, anomalies } = _resoudre(logFromCfg, opts);
  return { montant, anomalies };
}

/**
 * @param {object|null} logFromCfg — log résolu via flattenLogs (peut porter parentLog)
 * @param {{ nuits: number }} [opts] — durée du séjour : active les paliers
 * @returns {number|null} montant EUR > 0, ou null s'il n'y a pas de caution
 */
function resolveCautionFromLog(logFromCfg, opts) {
  return _resoudre(logFromCfg, opts).montant;
}

/**
 * Argent 1f (décision Dimitri 07/10) — la caution résolue est-elle un 0
 * VOULU (« pas de caution ») ? Même parcours que diagnostiquerCaution
 * (logement puis parent, négatif = champ vide signalé ailleurs) : true
 * seulement si le premier champ lisible vaut exactement 0. Sert à distinguer
 * « 0 € voulu » (réservable sans caution) de « non défini » (bloqué) : les
 * deux donnent montant null. Avec la durée : true si le palier du séjour
 * vaut 0 €.
 *
 * @param {object|null} logFromCfg — log résolu via flattenLogs (peut porter parentLog)
 * @param {{ nuits: number }} [opts] — durée du séjour : active les paliers
 * @returns {boolean}
 */
function cautionVoulueAZero(logFromCfg, opts) {
  return _resoudre(logFromCfg, opts).zero;
}

module.exports = {
  resolveCautionFromLog,
  diagnostiquerCaution,
  cautionVoulueAZero,
  lirePaliers,
  palierPourDuree,
  CAUTION_MONTANT_MAX_EUR,
};
